import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Save } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { CourseDetailsFields } from '../../components/training/course/CourseDetailsFields';
import { CourseTeachersFields } from '../../components/training/course/CourseTeachersFields';
import { CourseCostsFields } from '../../components/training/course/CourseCostsFields';
import { CourseCostSummary } from '../../components/training/course/CourseCostSummary';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { trainingCoursesStore } from '../../services/trainingCoursesStore';
import { emptyCourseDraft, parseCourseStatus } from '../../utils/trainingCourses';
import { CourseErrors, CourseTeacher, TrainingCourseDraft } from '../../types/trainingCourse';
export function CourseEditorPage() {
  const {
    courseId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courses = useTrainingCourses();
  const existing = courseId ? courses.find(c => c.id === courseId) : undefined;
  const [draft, setDraft] = useState<TrainingCourseDraft>(() => existing ? {
    ...existing,
    teachers: [...(existing.teachers || [])]
  } : emptyCourseDraft(parseCourseStatus(searchParams.get('stato'))));
  const [errors, setErrors] = useState<CourseErrors>({});
  if (courseId && !existing) return <Navigate to="/training/corsi" replace data-id="element-5986" data-mp="33zzh5lv8uahm" />;
  const update = (changes: Partial<TrainingCourseDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...changes
    }));
    setErrors(prev => {
      const next = {
        ...prev
      };
      Object.keys(changes).forEach(k => delete next[k]);
      if ('startDate' in changes) delete next.endDate;
      return next;
    });
  };
  const updateTeachers = (teachers: CourseTeacher[]) => {
    setDraft(prev => ({
      ...prev,
      teachers
    }));
    setErrors(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith('teacher-'))));
  };
  const validate = (): CourseErrors => {
    const next: CourseErrors = {};
    const isOnDemand = draft.status === 'ondemand';
    if (!draft.productCode) next.productCode = 'Seleziona tipologia di corso e codice prodotto';
    if (!draft.courseCode.trim()) next.courseCode = 'Inserisci il codice specifico del corso';
    if (draft.characteristic === 'Trainato' && !draft.leadCourseId) next.leadCourseId = 'Seleziona il corso trainante';
    if (!isOnDemand && draft.commission === 'Cliente' && !draft.clientName) next.clientName = "Seleziona l'azienda";
    if (!isOnDemand && draft.venue === 'cliente' && !draft.clientSite) next.clientSite = 'Seleziona la sede del cliente';
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) next.endDate = 'La data di fine non può precedere la data di inizio';
    (draft.teachers || []).forEach((t, i) => {
      if (!t.teacherId) next[`teacher-${i}`] = 'Seleziona il docente';
    });
    return next;
  };
  const handleSave = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const saved = trainingCoursesStore.save({
      ...draft,
      courseCode: draft.courseCode.trim()
    });
    navigate(`/training/corsi/${saved.id}`, {
      state: {
        saved: existing ? 'updated' : 'created'
      }
    });
  };
  const cancelHref = existing ? `/training/corsi/${existing.id}` : `/training/corsi?tab=${draft.status}`;
  const errorCount = Object.keys(errors).length;
  return <Layout data-id="element-5987" data-mp="81y9ekqitefg">
      <Link to={cancelHref} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-5988" data-mp="81pwwtyb8b4s">
        <ArrowLeft className="h-4 w-4" data-id="element-5989" data-mp="2su1jp7ptgeg8" />{' '}
        {existing ? 'Dashboard corso' : 'Corsi'}
      </Link>
      <div className="mb-6" data-id="element-5990" data-mp="383ew7ole7m4z">
        <h1 className="text-3xl font-bold text-seform-navy" data-id="element-5991" data-mp="384q4o1ps9i2f">
          {existing ? 'Modifica corso' : 'Nuovo corso'}
        </h1>
        {existing && <p className="text-slate-500 mt-1" data-id="element-5992" data-mp="2ycu48k085fv1">
            <span className="font-mono" data-id="element-5993" data-mp="2ydlnqwgjrn4y">{existing.courseCode}</span>
            {existing.name && ` · ${existing.name}`}
          </p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-5994" data-mp="3r2j24ohn0zsf">
        <div className="lg:col-span-2 space-y-6" data-id="element-5995" data-mp="3m2wqgjhgjrlq">
          <Card title="Dati corso" noHoverShadow data-id="element-5996" data-mp="3m47umketf1dd">
            <CourseDetailsFields draft={draft} onChange={update} errors={errors} courses={courses} data-id="element-5997" data-mp="3i1bagp9mmx7b" />
          </Card>
          <Card title="Docenti" noHoverShadow data-id="element-5998" data-mp="3d4kqvrym5jh5">
            <CourseTeachersFields teachers={draft.teachers || []} onChange={updateTeachers} errors={errors} productCode={draft.productCode} data-id="element-5999" data-mp="26n7drbbnmuyk" />
          </Card>
          <Card title="Costi" noHoverShadow data-id="element-6000" data-mp="h6ve95k4bjp3">
            <CourseCostsFields draft={draft} onChange={update} data-id="element-6001" data-mp="m3virc1bkee3" />
          </Card>
        </div>

        <aside className="lg:sticky lg:top-6 space-y-4" data-id="element-6002" data-mp="q5yyicv0u556">
          <CourseCostSummary course={draft} data-id="element-6003" data-mp="q4fgagpt1k0r" />
          {errorCount > 0 && <div role="alert" className="flex gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-id="element-6004" data-mp="ux8ml90x90ey">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-6005" data-mp="14umqk95nrwqd" />
              <p className="text-sm text-red-700" data-id="element-6006" data-mp="14ss6sy64ianv">
                {errorCount === 1 ? 'C’è 1 campo da correggere.' : `Ci sono ${errorCount} campi da correggere.`}
              </p>
            </div>}
          <div className="flex gap-3" data-id="element-6007" data-mp="3blkq4wfrjqtc">
            <Button variant="flat" color="default" className="flex-1" onClick={() => navigate(cancelHref)} data-id="element-6008" data-mp="3bmnjdb0k65cf">
              Annulla
            </Button>
            <Button className="flex-1" onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-6010" data-mp="hvfps4o5foln" />} data-id="element-6009" data-mp="muaa5b1rll7v">
              {existing ? 'Salva modifiche' : 'Crea corso'}
            </Button>
          </div>
        </aside>
      </div>
    </Layout>;
}